import type { ReactNode } from 'react';
import { AiAttributeListContainer } from '../AiAttributesList';
import { ProcessedAttribute } from '../utils/attributeValidation';
import { aiScoringResponseType } from '../api/aiScoring.api';
import { AttributeItem } from '../AiAttributesList/aiAttributeList.types';

export interface AiCandidateAttributesProps {
  aiScoringData?: aiScoringResponseType['data']['ai_scoring'];
  // Props from centralized hook
  attributes: ProcessedAttribute[];
  isLoading: boolean;
  isValid: boolean;
  isDirty: boolean;
  onAttributeChange: (id: number, newDescription: string) => void;
  onAttributeBlur: (id: number) => void;
  onAttributeDelete: (id: number) => void;
  onAttributeAdd: () => { newAttributeId: number };
  onAttributeReorder: (sourceIndex: number, destinationIndex: number) => void;
  onSaveAttributes: (shouldSave?: boolean) => Promise<any>;
  footerIsLoading: boolean;
  setActiveStep: (step: number) => void;
  // Props from ICP generation
  generatedAttributes?: AttributeItem[];
  setValidatedButNotSaved: (value: boolean) => void;
  onAttributeAutoFocus: (id: number) => void;
  /** Rendered below the attribute list and the Add attribute row
   * (e.g. the Screener auto-reject bar). */
  afterListContent?: ReactNode;
}

export const AiCandidateAttributes = ({
  aiScoringData: _aiScoringData,
  // Props from centralized hook
  attributes,
  isLoading: attributesLoading,
  isValid,
  isDirty,
  onAttributeChange,
  onAttributeBlur,
  onAttributeDelete,
  onAttributeAdd,
  onAttributeReorder,
  onSaveAttributes,
  footerIsLoading,
  setValidatedButNotSaved: _setValidatedButNotSaved,
  setActiveStep: _setActiveStep,
  // Props from ICP generation
  generatedAttributes,
  onAttributeAutoFocus,
  afterListContent,
}: AiCandidateAttributesProps) => {
  // Handler for explain click
  const handleExplainClick = async () => {
    if (onSaveAttributes && isDirty) {
      // If attributes are from generation and not yet saved, save them
      // This ensures the attributes are persisted when user navigates to step 2
      try {
        await onSaveAttributes(false); // Don't trigger retrigger
      } catch (error) {
        console.error('Error saving generated attributes:', error);
      }
    }
  };

  return (
    <AiAttributeListContainer
      attributes={
        Array.isArray(attributes) && attributes.length > 0 ? attributes : []
      }
      isLoading={attributesLoading}
      isValid={isValid}
      onAttributeChange={onAttributeChange}
      onAttributeBlur={onAttributeBlur}
      onAttributeDelete={onAttributeDelete}
      onAttributeAdd={onAttributeAdd}
      onAttributeReorder={onAttributeReorder}
      onExplainClick={handleExplainClick}
      footerIsLoading={footerIsLoading}
      onAttributeAutoFocus={onAttributeAutoFocus}
      generatedAttributes={generatedAttributes}
      afterListContent={afterListContent}
    />
  );
};
